/**
 * Concierge Pods — CMS page (PROD-6787).
 * Leads with the Concierge team (per-advisor limits, pod membership, reports, change history).
 * Region/country management lives on a separate page (/concierge-pods/regions), linked below.
 * Talks to the goserver CMS API at /v2/cms_user/concierge_pods/* (UserRoleOps).
 *
 * Styling note: this is a NEW page built MOBILE-FIRST (cards stack on small screens,
 * grid on lg+) and uses the dark-mode theme tokens from src/theme/tokens.css — it does
 * NOT follow the legacy desktop-table convention used by older pages.
 */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import moment from 'moment-timezone';
import CommonHeader from '../../util_components/common_header';
import CustomLoading from '../../util_components/custom_loading';
import Button from '../../util_components/button';
import CustomModal from '../../util_components/custom_modal';
import CustomSingleSelect from '../../util_components/custom_single_select';
import { get_api_url } from '../../../utils/urls';
import { get_api, post_api, put_api, delete_api } from '../../../redux/api_funcs';
import * as actions from '../../../redux/action_creators';

const BASE = '/v2/cms_user/concierge_pods';

// Theme-token styles (dark-mode aware — see src/theme/tokens.css).
const PAGE_STYLE = {
  background: 'var(--surface-page)',
  color: 'var(--text-primary)',
  minHeight: '100%'
};
const CARD_STYLE = { background: 'var(--surface-card)', borderColor: 'var(--border-default)' };
const CHIP_STYLE = {
  background: 'var(--surface-sunken)',
  color: 'var(--text-secondary)',
  borderColor: 'var(--border-default)'
};
const INPUT_STYLE = {
  background: 'var(--input-bg)',
  borderColor: 'var(--input-border)',
  color: 'var(--input-text)'
};
const MUTED_STYLE = { color: 'var(--text-muted)' };
const LINK_STYLE = { color: 'var(--brand-primary)' };
const ACTION_BTN = { padding: '6px 12px', fontSize: '13px' };

// Human labels for the change-history (audit) actions.
const AUDIT_ACTION_LABEL = {
  region_create: 'Created pod',
  region_update: 'Updated pod',
  country_add: 'Added country',
  country_remove: 'Removed country',
  teacher_assign: 'Assigned advisor',
  teacher_unassign: 'Removed advisor',
  limits_update: 'Updated limits'
};

class ConciergePods extends Component {
  state = {
    loading: false,
    regions: [],
    teachers: [],
    // change-history (audit) modal
    show_audit_modal: false,
    audit_loading: false,
    audit_data: [],
    audit_title: ''
  };

  componentDidMount() {
    this.load_all();
  }

  load_all = () => {
    this.setState({ loading: true });
    // regions feed the per-advisor pod labels + the "Add to pod" select; teachers are the team.
    Promise.all([
      get_api(get_api_url(`${BASE}/regions`), true),
      get_api(get_api_url(`${BASE}/concierge_teachers`), true)
    ])
      .then(([r1, r2]) => {
        this.setState({
          regions: (r1.data && r1.data.data) || [],
          teachers: (r2.data && r2.data.data) || [],
          loading: false
        });
      })
      .catch((e) => {
        this.show_error(e, 'Failed to load concierge team');
        this.setState({ loading: false });
      });
  };

  notify = (type, msg) => {
    const { set_notification_variable } = this.props;
    set_notification_variable(true, type, msg);
  };

  show_error = (e, fallback) => {
    let msg = fallback;
    if (e && e.response && e.response.data) {
      msg = e.response.data.reason || e.response.data.message || fallback;
    }
    this.notify('error', msg);
  };

  // ── Advisors ───────────────────────────────────────────────────────────────

  assign_teacher = (region_id, teacher_uuid) => {
    if (!region_id) return;
    post_api(get_api_url(`${BASE}/regions/${region_id}/teachers`), { teacher_uuid }, true)
      .then(() => {
        this.notify('success', 'Advisor assigned');
        this.load_all();
      })
      .catch((e) => this.show_error(e, 'Failed to assign advisor'));
  };

  unassign_teacher = (region_id, teacher_uuid) => {
    delete_api(get_api_url(`${BASE}/regions/${region_id}/teachers/${teacher_uuid}`), true)
      .then(() => {
        this.notify('success', 'Advisor removed');
        this.load_all();
      })
      .catch((e) => this.show_error(e, 'Failed to remove advisor'));
  };

  on_limit_change = (uuid, field, value) => {
    this.setState((prev) => ({
      teachers: prev.teachers.map((t) => (t.uuid === uuid ? { ...t, [field]: value } : t))
    }));
  };

  save_limits = (t) => {
    put_api(
      get_api_url(`${BASE}/concierge_teachers/${t.uuid}/limits`),
      {
        concierge_per_day_limit: Number(t.concierge_per_day_limit) || 0,
        concierge_per_30_days_limit: Number(t.concierge_per_30_days_limit) || 0
      },
      true
    )
      .then(() => {
        this.notify('success', 'Limits saved');
        this.load_all();
      })
      .catch((e) => this.show_error(e, 'Failed to save limits'));
  };

  region_name_by_id = (id) => {
    const { regions } = this.state;
    const r = regions.find((x) => x.id === id);
    return r ? r.name : `#${id}`;
  };

  open_report = (t) => {
    const { history } = this.props;
    history.push(`/concierge-pods/report?teacher=${encodeURIComponent(t.uuid)}`);
  };

  open_audit = (title, url) => {
    this.setState({
      show_audit_modal: true,
      audit_loading: true,
      audit_data: [],
      audit_title: title
    });
    get_api(get_api_url(url), true)
      .then((res) =>
        this.setState({ audit_data: (res.data && res.data.data) || [], audit_loading: false })
      )
      .catch((e) => {
        this.show_error(e, 'Failed to load change history');
        this.setState({ audit_loading: false });
      });
  };

  close_audit_modal = () => {
    this.setState({ show_audit_modal: false, audit_data: [] });
  };

  // ── Sub-renders (mobile-first cards) ─────────────────────────────────────────

  render_chip = (chip_key, label, onRemove, title) => (
    <button
      key={chip_key}
      type="button"
      onClick={onRemove}
      title={title}
      className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs border"
      style={CHIP_STYLE}
    >
      {label} <span aria-hidden="true">✕</span>
    </button>
  );

  render_teachers = () => {
    const { teachers, regions } = this.state;
    return (
      <section>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">Concierge team</h2>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
            <Link to="/concierge-pods/regions" style={LINK_STYLE}>
              Regions &amp; countries &raquo;
            </Link>
            <Link to="/concierge-pods/team-assignments" style={LINK_STYLE}>
              Team assignments &raquo;
            </Link>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {teachers.map((t) => (
            <div key={t.uuid} className="rounded-lg border p-4" style={CARD_STYLE}>
              <div className="flex items-center justify-between gap-2">
                <a
                  href={`/teacherprofile?id=${encodeURIComponent(t.uuid)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold"
                  style={LINK_STYLE}
                >
                  {t.first_name} {t.last_name}
                </a>
                <div className="flex shrink-0 items-center gap-2">
                  <Button onClick={() => this.open_report(t)} custom_style={ACTION_BTN}>
                    30-day report
                  </Button>
                  <Button
                    onClick={() =>
                      this.open_audit(
                        `${t.first_name} ${t.last_name} — change history`,
                        `${BASE}/concierge_teachers/${t.uuid}/audit`
                      )
                    }
                    custom_style={ACTION_BTN}
                  >
                    History
                  </Button>
                </div>
              </div>
              <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end">
                <label className="text-xs" style={MUTED_STYLE}>
                  Per-day limit
                  <input
                    type="number"
                    aria-label="Per-day limit"
                    value={t.concierge_per_day_limit}
                    onChange={(e) =>
                      this.on_limit_change(t.uuid, 'concierge_per_day_limit', e.target.value)
                    }
                    className="mt-1 block w-full rounded border px-2 py-1.5 text-sm sm:w-24"
                    style={INPUT_STYLE}
                  />
                </label>
                <label className="text-xs" style={MUTED_STYLE}>
                  Per-30-day limit
                  <input
                    type="number"
                    aria-label="Per-30-day limit"
                    value={t.concierge_per_30_days_limit}
                    onChange={(e) =>
                      this.on_limit_change(t.uuid, 'concierge_per_30_days_limit', e.target.value)
                    }
                    className="mt-1 block w-full rounded border px-2 py-1.5 text-sm sm:w-24"
                    style={INPUT_STYLE}
                  />
                </label>
                <Button onClick={() => this.save_limits(t)} custom_style={ACTION_BTN}>
                  Save limits
                </Button>
              </div>
              <div className="mt-3">
                <div className="mb-1 text-xs" style={MUTED_STYLE}>
                  Pods
                </div>
                <div className="flex flex-wrap gap-1">
                  {(t.region_ids || []).length === 0 && (
                    <span className="text-xs" style={MUTED_STYLE}>
                      None
                    </span>
                  )}
                  {(t.region_ids || []).map((rid) =>
                    this.render_chip(
                      rid,
                      this.region_name_by_id(rid),
                      () => this.unassign_teacher(rid, t.uuid),
                      'Remove from pod'
                    )
                  )}
                </div>
              </div>
              <div className="mt-3">
                <CustomSingleSelect
                  options={regions
                    .filter((r) => !!r.is_active && !(t.region_ids || []).includes(r.id))
                    .map((r) => ({ value: String(r.id), label: r.name }))}
                  name={`assign_${t.uuid}`}
                  value=""
                  noLabel
                  defaultOption="Add to pod…"
                  onChange={(e) => this.assign_teacher(Number(e.target.value), t.uuid)}
                  style={{ width: '100%', maxWidth: '220px' }}
                />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  };

  render_audit_modal = () => {
    const { show_audit_modal, audit_loading, audit_data, audit_title } = this.state;
    if (!show_audit_modal) return null;
    return (
      <CustomModal show close={this.close_audit_modal}>
        <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
          {audit_title}
        </h3>
        {audit_loading && <CustomLoading />}
        {!audit_loading && (
          <div className="mt-3 flex max-h-96 flex-col gap-2 overflow-y-auto">
            {audit_data.map((a) => (
              <div key={a.id} className="rounded border p-2" style={CARD_STYLE}>
                <div className="text-sm font-medium">
                  {AUDIT_ACTION_LABEL[a.action] || a.action}
                  {a.teacher_name ? ` — ${a.teacher_name}` : ''}
                  {a.region_name ? ` (${a.region_name})` : ''}
                </div>
                <div className="mt-0.5 text-xs" style={MUTED_STYLE}>
                  {a.detail ? `${a.detail} · ` : ''}
                  {a.actor_name || a.actor_uuid || 'unknown'} ·{' '}
                  {moment(a.created_date).tz('Asia/Kolkata').format('MMM D, YYYY h:mm A [IST]')}
                </div>
              </div>
            ))}
            {audit_data.length === 0 && (
              <div className="text-xs" style={MUTED_STYLE}>
                No changes recorded yet.
              </div>
            )}
          </div>
        )}
      </CustomModal>
    );
  };

  render() {
    const { loading } = this.state;
    return (
      <div style={PAGE_STYLE}>
        <CommonHeader title="Concierge Pods" loading={loading} />
        {loading ? (
          <CustomLoading />
        ) : (
          <div className="mx-auto max-w-5xl px-3 py-4 sm:px-6">
            {this.render_teachers()}
            {this.render_audit_modal()}
          </div>
        )}
      </div>
    );
  }
}

ConciergePods.propTypes = {
  set_notification_variable: PropTypes.func.isRequired,
  history: PropTypes.shape({ push: PropTypes.func.isRequired }).isRequired
};

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) => ({
  set_notification_variable: (show, type, mes) =>
    dispatch(actions.set_notification_variable(show, type, mes))
});

export default connect(mapStateToProps, mapDispatchToProps)(ConciergePods);
